import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Loader2, AlertCircle, CheckCircle2, Building2 } from 'lucide-react';

export default function InvitePage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) { setError("Lien d'invitation invalide"); setLoading(false); return; }
        axios.get(`${API_URL}/api/auth/invite-info?token=${token}`)
            .then(res => setInfo(res.data))
            .catch(err => setError(err.response?.data?.detail || "Invitation invalide ou expirée"))
            .finally(() => setLoading(false));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault(); setError('');
        if (password !== passwordConfirm) { setError('Les mots de passe ne correspondent pas'); return; }
        if (password.length < 6) { setError('Minimum 6 caractères'); return; }
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/auth/accept-invite`, { token, password }, { withCredentials: true });
            setDone(true);
            setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
        } catch (err) {
            setError(err.response?.data?.detail || 'Erreur');
        }
        setSaving(false);
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-6">
            <div className="w-full max-w-md space-y-6 animate-fade-in">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-5 bg-[#6366F1]">
                        <span className="text-xl font-bold text-white font-['Inter']">M</span>
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-900">Marge Auto</h1>
                </div>

                {done ? (
                    <div className="card p-8 text-center">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Compte activé !</h2>
                        <p className="text-sm text-gray-500">Redirection vers votre espace...</p>
                    </div>
                ) : error && !info ? (
                    <div className="card p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Invitation invalide</h2>
                        <p className="text-sm text-gray-500 mb-4">{error}</p>
                        <Button onClick={() => navigate('/auth')} variant="outline">Retour à la connexion</Button>
                    </div>
                ) : info ? (
                    <div className="card p-6">
                        <div className="text-center mb-6">
                            <h2 className="text-lg font-semibold text-gray-900">Bienvenue {info.name}</h2>
                            <p className="text-sm text-gray-500 mt-1">Vous avez été invité(e) à rejoindre</p>
                            <div className="inline-flex items-center gap-2 mt-2 bg-gray-50 rounded-lg px-3 py-1.5">
                                <Building2 className="w-4 h-4 text-[#6366F1]" />
                                <span className="text-sm font-medium text-gray-700">{info.agency_name}</span>
                            </div>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Email</Label>
                                <Input type="email" value={info.email} disabled className="bg-gray-50 text-gray-500" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Choisissez votre mot de passe</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="password" placeholder="Minimum 6 caractères" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" required minLength={6} data-testid="invite-password" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Confirmer le mot de passe</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="password" placeholder="••••••" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} className="pl-10" required minLength={6} data-testid="invite-password-confirm" />
                                </div>
                            </div>
                            <Button type="submit" className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white" disabled={saving} data-testid="invite-submit-btn">
                                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Activer mon compte
                            </Button>
                        </form>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
